namespace RS.Util
{
    export type KeyCodeCallback = ((keyCode: KeyCode) => void);
    export type KeyCodeMap = { [name: string]: KeyCode };

    // Used to generate new hotkeys and alias as keycodes
    const keyMap: { [name: string]: string } = {};

    /**
     * Based on .key value as described here
     * https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key/Key_Values
     */
    export const KeyConstants =
    {
        BACKSPACE   : keyMap["BACKSPACE"]    = "Backspace",
        TAB         : keyMap["TAB"]          = "Tab",
        ENTER       : keyMap["ENTER"]        = "Enter",
        PAUSE       : keyMap["PAUSE"]        = "Pause",
        CAPSLOCK    : keyMap["CAPSLOCK"]     = "CapsLock",
        ESCAPE      : keyMap["ESCAPE"]       = "Escape",
        SPACE       : keyMap["SPACE"]        = "Space",
        PAGE_UP     : keyMap["PAGE_UP"]      = "PageUp",
        PAGE_DOWN   : keyMap["PAGE_DOWN"]    = "PageDown",
        END         : keyMap["END"]          = "End",
        HOME        : keyMap["HOME"]         = "Home",
        LEFT        : keyMap["LEFT"]         = "ArrowLeft",
        UP          : keyMap["UP"]           = "ArrowUp   ",
        RIGHT       : keyMap["RIGHT"]        = "ArrowRight",
        DOWN        : keyMap["DOWN"]         = "ArrowDown",
        PRINT_SCREEN: keyMap["PRINT_SCREEN"] = "PrintScreen",
        INSERT      : keyMap["INSERT"]       = "Insert",
        DELETE      : keyMap["DELETE"]       = "Delete",
        A           : keyMap["A"]            = "a",
        B           : keyMap["B"]            = "b",
        C           : keyMap["C"]            = "c",
        D           : keyMap["D"]            = "d",
        E           : keyMap["E"]            = "e",
        F           : keyMap["F"]            = "f",
        G           : keyMap["G"]            = "g",
        H           : keyMap["H"]            = "h",
        I           : keyMap["I"]            = "i",
        J           : keyMap["J"]            = "j",
        K           : keyMap["K"]            = "k",
        L           : keyMap["L"]            = "l",
        M           : keyMap["M"]            = "m",
        N           : keyMap["N"]            = "n",
        O           : keyMap["O"]            = "o",
        P           : keyMap["P"]            = "p",
        Q           : keyMap["Q"]            = "q",
        R           : keyMap["R"]            = "r",
        S           : keyMap["S"]            = "s",
        T           : keyMap["T"]            = "t",
        U           : keyMap["U"]            = "u",
        V           : keyMap["V"]            = "v",
        W           : keyMap["W"]            = "w",
        X           : keyMap["X"]            = "x",
        Y           : keyMap["Y"]            = "y",
        Z           : keyMap["Z"]            = "z",
        F1          : keyMap["F1"]           = "F1",
        F2          : keyMap["F2"]           = "F2",
        F3          : keyMap["F3"]           = "F3",
        F4          : keyMap["F4"]           = "F4",
        F5          : keyMap["F5"]           = "F5",
        F6          : keyMap["F6"]           = "F6",
        F7          : keyMap["F7"]           = "F7",
        F8          : keyMap["F8"]           = "F8",
        F9          : keyMap["F9"]           = "F9",
        F10         : keyMap["F10"]          = "F10",
        F11         : keyMap["F11"]          = "F11",
        F12         : keyMap["F12"]          = "F12"
    }

    export enum KeyPressType
    {
        KeyPressed,
        KeyDown,
        KeyUp
    }

    export class HotKey
    {
        /** Key Code to match DOM KeyBoardEvent key value */
        public readonly keyCode: string;
        /** Optional callback can be set */
        public readonly callback: KeyCodeCallback;
        /** If this needs alt held */
        public readonly alt: boolean;
        /** If this needs ctrl held */
        public readonly ctrl: boolean;
        /** If this needs cmd held */
        public readonly cmd: boolean;
        /** If the game needs visibility to be pressable */
        public readonly needVisibility: boolean;

        public constructor(keyCode: string, callback?: KeyCodeCallback, needVisibility: boolean = false, alt: boolean = false, ctrl: boolean = false, cmd: boolean = false)
        {
            this.keyCode = keyCode;
            this.callback = callback;
            this.needVisibility = needVisibility;
            this.alt = alt;
            this.ctrl = ctrl;
            this.cmd = cmd;
        }

        /**
         * Check if this hot key matched a dom event
         * @param keyboardEvent Event from DOM to match
         */
        public shouldHitHotKey(keyboardEvent: KeyboardEvent): boolean
        {
            const matchedKey = (keyboardEvent.key === this.keyCode || keyboardEvent.code === this.keyCode);
            const notMatchedAlt = keyboardEvent.altKey != this.alt;
            const notMatchedCtrl = keyboardEvent.ctrlKey != this.ctrl;
            const notMatchedCmd = keyboardEvent.metaKey != this.cmd;

            const height = document.documentElement.clientHeight;
            const width = document.documentElement.clientWidth;
            const currentElement = document.elementFromPoint((width/2), (height/2));
            const notMatchedVisibility = this.needVisibility && !(currentElement && currentElement.classList.contains("rs-cmp") && !currentElement.classList.contains("rs-cover"));

            // Fail if we wanted to match alt/ctrl/cmd/visibility but didnt
            if (notMatchedCtrl || notMatchedAlt || notMatchedCmd || notMatchedVisibility)
            {
                return false;
            }

            return matchedKey;
        }

        /**
         * Clone this object with optional new params
         * @param callback Optional clone wiht new callback
         * @param alt Optional clone wiht alt value
         * @param ctrl Optional clone wiht ctrl value
         */
        public cloneWith(callback?: KeyCodeCallback, needVisibility?: boolean, alt?: boolean, ctrl?: boolean, cmd?: boolean): HotKey
        {
            return new HotKey(
                this.keyCode,
                callback !== undefined ? callback : this.callback,
                needVisibility !== undefined ? needVisibility : this.needVisibility,
                alt !== undefined ? alt : this.alt,
                ctrl !== undefined ? ctrl : this.ctrl,
                cmd !== undefined ? cmd : this.cmd
            );
        }
    }

    function mapObject<K extends string, T, U>(obj: Record<K, T>, f: (x: T) => U): Record<K, U>
    {
        const tmp = {} as Record<K, U>;
        for (const key in obj)
        {
            tmp[key] = f(obj[key]);
        }
        return tmp;
    }

    export type KeyCode = HotKey;
    export const KeyCodes = mapObject(KeyConstants, (key: string) => new HotKey(key, null, true));
}